import { todayLocalDay, localDayOf } from "../../shared/format/local-date.js";
import { createIcons, Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, Link as LinkIcon, Power, ArrowRight, Gauge, Cpu, Calendar, ShieldCheck, Wrench } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import type { Instrument, InstrumentType, InstrumentStatus, Equipment } from "../../equipment/equipment-domain.js";
import type { InstrumentListFilters } from "../../equipment/instrument-service.js";
import type { AreaId, EquipmentId } from "../../shared/kernel/identifiers.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { ContextLoadTracker, CONTEXT_LOAD_STYLES } from "../context/context-load.js";
import {
  areasForCascade,
  changeCascade,
  EMPTY_CASCADE,
  equipmentsForCascade,
  LatestRequestGate,
  projectsForCascade,
  type CascadeLevel,
  type CascadeSelection
} from "../context/client-cascade.js";
import {
  AssetCascadeFields,
  type AssetCascadeSources,
  type CascadeAreaOption,
  type CascadeClientOption,
  type CascadeProjectOption
} from "../components/asset-cascade-fields.js";

class AppInstrumentsPage extends HTMLElement {
  private instruments: Instrument[] = [];
  /** Equipamentos do recorte da listagem (Cliente filtrado ou todos os autorizados). */
  private equipments: Equipment[] = [];
  /** Cache por Cliente — o formulário pode escolher um Cliente diferente do filtro. */
  private readonly equipmentsByClient = new Map<string, Equipment[]>();
  private clients: CascadeClientOption[] = [];
  private projects: CascadeProjectOption[] = [];
  private unitAreas: CascadeAreaOption[] = [];
  private isLoading = true;
  private searchTerm = "";
  private typeFilter = "ALL";
  private statusFilter = "ALL";
  private calibFilter = "ALL";
  /** Filtro local CLIENTE → PROJETO → UNIDADE/ÁREA → EQUIPAMENTO (não depende do cabeçalho global). */
  private filter: CascadeSelection = EMPTY_CASCADE;
  private readonly ctxLoad = new ContextLoadTracker();
  /** A consulta de A que responder depois da de B não desenha por cima de B. */
  private readonly listGate = new LatestRequestGate();
  private unsubscribe: (() => void) | null = null;

  connectedCallback() {
    void this.loadReferenceData().then(() => this.loadData());
    this.unsubscribe = contextStore.subscribe(() => {
      void this.loadData();
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /** Contexto da chamada com o Cliente LOCAL da tela; vazio = Todos os Clientes autorizados. */
  private ctxFor(clientId: string) {
    return { ...sessionRequestContext(), activeClientId: clientId || undefined };
  }

  private get authorizedClientIds(): ReadonlySet<string> {
    return new Set(this.clients.map(c => c.id));
  }

  /** Clientes autorizados, Projetos e Unidades/Áreas — dados de referência da cascata. */
  private async loadReferenceData() {
    const p = window.app.platform;
    const ctx = this.ctxFor("");
    const clients = await this.ctxLoad.load("Clientes", () => p.adminService.listAuthorizedClients(ctx));
    this.clients = clients.map(c => ({ id: String(c.id), name: c.name || c.legalName || String(c.id) }));
    const projects = await this.ctxLoad.load("Projetos", () => p.projectService.listProjects(undefined, undefined, ctx));
    this.projects = projects.map(pr => ({ id: String(pr.id), code: pr.code || "", name: pr.name || "", clientId: String(pr.clientId || "") }));
    const areas = await this.ctxLoad.load("Unidades / Áreas", () => p.projectService.listUnitAreas({}, undefined, ctx));
    this.unitAreas = areas.map(a => ({ id: String(a.id), code: a.code || "", name: a.name || "", clientId: String(a.clientId || ""), projectId: String(a.projectId || "") }));
  }

  /** Equipamentos de um Cliente, com cache — usado pela cascata do formulário. */
  private async equipmentsOf(clientId: string): Promise<Equipment[]> {
    const cached = this.equipmentsByClient.get(clientId);
    if (cached) return cached;
    const list = await this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(this.ctxFor(clientId)));
    this.equipmentsByClient.set(clientId, list);
    return list;
  }

  private cascadeSources(): AssetCascadeSources {
    return {
      clients: this.clients,
      projects: this.projects,
      areas: this.unitAreas,
      authorizedClientIds: this.authorizedClientIds,
      equipmentsOf: clientId => this.equipmentsOf(clientId)
    };
  }

  private async loadData() {
    const ticket = this.listGate.begin();
    this.isLoading = true;
    this.render();

    try {
      // O recorte (Projeto, Unidade/Área, Equipamento) vai ao SERVIÇO, que
      // consulta só o que pertence a ele; o Cliente vai no contexto.
      const ctx = this.ctxFor(this.filter.clientId);
      const filters: InstrumentListFilters = {};
      if (this.filter.projectId) filters.projectId = this.filter.projectId;
      if (this.filter.areaId) filters.areaId = this.filter.areaId;
      if (this.filter.equipmentId) filters.equipmentId = this.filter.equipmentId;

      const [insts, eqs] = await Promise.all([
        window.app.platform.instrumentService.listInstruments(ctx, filters),
        this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(ctx))
      ]);
      if (!this.listGate.isCurrent(ticket)) return;

      this.instruments = insts;
      this.equipments = eqs;
      for (const clientId of new Set(eqs.map(e => String(e.clientId)))) {
        this.equipmentsByClient.set(clientId, eqs.filter(e => String(e.clientId) === clientId));
      }
      this.isLoading = false;
      this.render();
    } catch (e) {
      if (!this.listGate.isCurrent(ticket)) return;
      console.error("[INSTRUMENTOS] Falha ao carregar a listagem", e);
      this.isLoading = false;
      this.renderError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Ações sobre um instrumento existente usam o Cliente DELE — não o filtro nem a sessão. */
  private ctxForInstrument(id: string) {
    const inst = this.instruments.find(i => i.id === id);
    return this.ctxFor(inst ? String(inst.clientId) : this.filter.clientId);
  }

  private errorText(err: unknown): string {
    if (err && typeof err === "object" && "safeMessage" in err && typeof err.safeMessage === "string" && err.safeMessage) return err.safeMessage;
    return err instanceof Error ? err.message : String(err);
  }

  private clientNameOf(clientId: string): string {
    return this.clients.find(c => c.id === String(clientId))?.name ?? String(clientId);
  }

  private getFilteredInstruments(): Instrument[] {
    return this.instruments.filter(inst => {
      const matchesSearch = !this.searchTerm ||
        inst.tag.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        inst.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (inst.service && inst.service.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (inst.manufacturer && inst.manufacturer.toLowerCase().includes(this.searchTerm.toLowerCase()));

      const matchesType = this.typeFilter === 'ALL' || inst.instrumentType === this.typeFilter;
      const matchesStatus = this.statusFilter === 'ALL' || inst.status === this.statusFilter;

      let matchesCalib = true;
      if (this.calibFilter !== 'ALL') {
        matchesCalib = inst.calibrationStatus === this.calibFilter;
      }

      return matchesSearch && matchesType && matchesStatus && matchesCalib;
    });
  }

  private getStatusBadge(status: InstrumentStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'INACTIVE':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-danger-solid);"></span> INATIVO</span>`;
      case 'OUT_OF_SERVICE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-warning-text);"></span> FORA DE OPERAÇÃO</span>`;
      case 'CALIBRATION_DUE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-warning-text);"></span> CALIBRAÇÃO VENCIDA</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${status}</span>`;
    }
  }

  private getCalibrationBadge(status: string | null | undefined): string {
    switch (status) {
      case 'VALID':
        return `<span style="background: rgba(34, 197, 94, 0.12); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.25); padding: 2px 6px; border-radius: 4px; font-size: 0.7rem; font-weight: 600;">VÁLIDA</span>`;
      case 'EXPIRED':
      case 'DUE':
        return `<span style="background: rgba(239, 68, 68, 0.12); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.25); padding: 2px 6px; border-radius: 4px; font-size: 0.7rem; font-weight: 600;">VENCIDA</span>`;
      case 'NOT_REQUIRED':
        return `<span style="background: var(--color-surface-secondary); color: var(--color-text-disabled); padding: 2px 6px; border-radius: 4px; font-size: 0.7rem;">NÃO REQUER</span>`;
      default:
        return `<span style="color: var(--color-text-primary); font-size: 0.75rem;">Pendente</span>`;
    }
  }

  private getTypeLabel(type: InstrumentType): string {
    const map: Record<string, string> = {
      'PRESSURE_GAUGE': 'Manômetro (PI/PG)',
      'PRESSURE_TRANSMITTER': 'Transmissor de Pressão (PT)',
      'TEMPERATURE_INDICATOR': 'Termômetro (TI)',
      'TEMPERATURE_TRANSMITTER': 'Transmissor de Temperatura (TT)',
      'LEVEL_INDICATOR': 'Visor de Nível (LI/LG)',
      'LEVEL_TRANSMITTER': 'Transmissor de Nível (LT)',
      'FLOW_METER': 'Medidor de Vazão (FI)',
      'FLOW_TRANSMITTER': 'Transmissor de Vazão (FT)',
      'SWITCH': 'Pressostato / Termostato',
      'THERMOCOUPLE': 'Termopar (TE)',
      'RTD': 'Termorresistência (RTD Pt100)',
      'DIFFERENTIAL_PRESSURE': 'Transmissor Pressão Dif. (PDT)',
      'OTHER': 'Outro Instrumento'
    };
    return map[type] || type;
  }

  private esc(value: string): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  /** CLIENTE → PROJETO → UNIDADE/ÁREA → EQUIPAMENTO da listagem; cada nível só mostra o compatível com os de cima. */
  private renderCascadeFilters(): string {
    const sel = this.filter;
    const authorized = this.authorizedClientIds;
    const projects = projectsForCascade(this.projects, sel, authorized);
    const areas = areasForCascade(this.unitAreas, sel, authorized);
    const equipments = equipmentsForCascade(this.equipments, sel);
    return `
      <select class="inst-select" data-filter="clientId" aria-label="Cliente" style="min-width: 200px;">
        <option value="">${this.esc(this.ctxLoad.emptyLabel("Clientes", "Nenhum Cliente autorizado", "Todos os Clientes"))}</option>
        ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${sel.clientId === c.id ? 'selected' : ''}>${this.esc(c.name)}</option>`).join('')}
      </select>
      <select class="inst-select" data-filter="projectId" aria-label="Projeto" style="min-width: 200px;">
        <option value="">Todos os Projetos</option>
        ${projects.map(p => `<option value="${this.esc(p.id)}" ${sel.projectId === p.id ? 'selected' : ''}>${this.esc(p.code)} — ${this.esc(p.name)}${sel.clientId ? '' : ` (${this.esc(this.clientNameOf(p.clientId))})`}</option>`).join('')}
      </select>
      <select class="inst-select" data-filter="areaId" aria-label="Unidade / Área" style="min-width: 180px;">
        <option value="">Todas as Unidades / Áreas</option>
        ${areas.map(a => `<option value="${this.esc(a.id)}" ${sel.areaId === a.id ? 'selected' : ''}>${this.esc([a.code, a.name].filter(Boolean).join(' - '))}</option>`).join('')}
      </select>
      <select class="inst-select" data-filter="equipmentId" aria-label="Equipamento" style="min-width: 180px;">
        <option value="">Todos os Equipamentos</option>
        ${equipments.map(e => `<option value="${this.esc(e.id)}" ${sel.equipmentId === e.id ? 'selected' : ''}>${this.esc(e.tag)} - ${this.esc(e.name)}</option>`).join('')}
      </select>`;
  }

  private renderError(msg: string) {
    this.innerHTML = `
      <div style="padding: 32px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; margin: 24px; color: var(--status-danger-solid);">
        <h3 style="margin: 0 0 8px 0; font-size: 1.1rem;">Erro ao carregar Instrumentos</h3>
        <p style="margin: 0 0 16px 0; color: var(--color-text-disabled);">${msg}</p>
        <button id="btn-retry" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--ia-line, #52525b); padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Tentar Novamente
        </button>
      </div>
    `;
    this.querySelector('#btn-retry')?.addEventListener('click', () => this.loadData());
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando cadastro de instrumentos...</span>
        </div>
      `;
      return;
    }

    const filtered = this.getFilteredInstruments();
    const canCreate = contextStore.hasPermission(PermissionCode.InstrumentCreate);
    const canDelete = contextStore.hasPermission(PermissionCode.InstrumentDelete);
    const canUpdate = contextStore.hasPermission(PermissionCode.InstrumentUpdate);
    // Com "Todos os Clientes" a mesma TAG pode existir em Clientes diferentes:
    // a coluna Cliente distingue.
    const showClientColumn = !this.filter.clientId;

    this.innerHTML = `
      <style>
        .inst-page-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          color: var(--color-text-primary);
        }
        .inst-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .inst-title-group h1 {
          font-size: 1.4rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .inst-subtitle {
          font-size: 0.85rem;
          color: var(--color-text-primary);
        }
        .inst-toolbar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: center;
          background: var(--ia-bg-panel, #18181b);
          padding: 14px;
          border-radius: 8px;
          border: 1px solid var(--ia-line, #27272a);
        }
        .inst-search-box {
          flex: 1;
          min-width: 220px;
          position: relative;
        }
        .inst-search-box input {
          width: 100%;
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px 8px 34px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          box-sizing: border-box;
        }
        .inst-search-box i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-primary);
          pointer-events: none;
        }
        .inst-select {
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          min-width: 150px;
        }
        .btn-primary-inst {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, var(--color-text-primary));
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .btn-primary-inst:hover {
          background: var(--color-primary);
        }
        .btn-secondary-inst {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-primary);
          border: 1px solid var(--color-input-border);
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s;
        }
        .btn-secondary-inst:hover {
          background: var(--ia-bg-raised, #3f3f46);
        }
        .inst-table-wrapper {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          overflow-x: auto;
        }
        .inst-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
          text-align: left;
        }
        .inst-table th {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-disabled);
          padding: 12px 16px;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--ia-line, #3f3f46);
        }
        .inst-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #27272a;
          color: var(--color-text-primary);
        }
        .inst-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }
        .inst-tag-cell {
          font-weight: 600;
          color: var(--color-text-muted);
          font-family: monospace;
          font-size: 0.95rem;
        }
        .inst-action-btn {
          background: var(--ia-bg-panel, #27272a);
          border: 1px solid var(--color-input-border);
          color: var(--color-text-primary);
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: background 0.15s;
          margin-right: 4px;
        }
        .inst-action-btn:hover {
          background: var(--ia-bg-raised, #3f3f46);
          color: var(--color-text-primary);
        }
        .inst-action-btn.danger:hover {
          background: var(--status-danger-solid);
          color: var(--color-text-primary);
        }
        .inst-action-btn.warning:hover {
          background: var(--status-warning-text);
          color: var(--color-text-primary);
        }
        .inst-stat-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        .inst-stat-card {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .inst-stat-num {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .inst-stat-label {
          font-size: 0.75rem;
          color: var(--color-text-primary);
          text-transform: uppercase;
        }
        ${CONTEXT_LOAD_STYLES}
      </style>

      <div class="inst-page-container">
        <!-- HEADER -->
        <div class="inst-header">
          <div class="inst-title-group">
            <h1><i data-lucide="gauge" style="color: var(--color-text-muted); width: 24px; height: 24px;"></i> Cadastro de Instrumentos</h1>
            <!-- [CORREÇÃO PONTUAL] Removido texto residual "Cliente Ativo". O filtro local é a única referência visual de Cliente. -->
            <div class="inst-subtitle">Manômetros, Termômetros, Transmissores e Calibração</div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary-inst" id="btn-refresh-list">
              <i data-lucide="refresh-cw" style="width: 16px; height: 16px;"></i> Atualizar
            </button>
            <button class="btn-secondary-inst" id="btn-goto-hub">
              <i data-lucide="link" style="width: 16px; height: 16px;"></i> Hub Técnico
            </button>
            ${canCreate ? `
              <button class="btn-primary-inst" id="btn-new-inst">
                <i data-lucide="plus" style="width: 18px; height: 18px;"></i> Novo Instrumento
              </button>
            ` : ''}
          </div>
        </div>

        <!-- STATS -->
        <div class="inst-stat-cards">
          <div class="inst-stat-card">
            <div>
              <div class="inst-stat-label">Total Instrumentos</div>
              <div class="inst-stat-num">${this.instruments.length}</div>
            </div>
            <i data-lucide="gauge" style="width: 28px; height: 28px; color: var(--color-text-muted); ;"></i>
          </div>
          <div class="inst-stat-card">
            <div>
              <div class="inst-stat-label">Calibração em Dia</div>
              <div class="inst-stat-num" style="color: var(--status-success-text);">${this.instruments.filter(i => i.calibrationStatus === 'VALID').length}</div>
            </div>
            <i data-lucide="shield-check" style="width: 28px; height: 28px; color: var(--status-success-text); ;"></i>
          </div>
          <div class="inst-stat-card">
            <div>
              <div class="inst-stat-label">Calibração Vencida</div>
              <div class="inst-stat-num" style="color: var(--status-danger-solid);">${this.instruments.filter(i => i.calibrationStatus === 'EXPIRED' || i.calibrationStatus === 'DUE').length}</div>
            </div>
            <i data-lucide="alert-triangle" style="width: 28px; height: 28px; color: var(--status-danger-solid); ;"></i>
          </div>
          <div class="inst-stat-card">
            <div>
              <div class="inst-stat-label">Vinculados a Equipamentos</div>
              <div class="inst-stat-num" style="color: var(--ia-info-fg, #60a5fa);">${this.instruments.filter(i => !!i.equipmentId).length}</div>
            </div>
            <i data-lucide="link" style="width: 28px; height: 28px; color: var(--ia-info-fg, #60a5fa); ;"></i>
          </div>
        </div>

        <!-- TOOLBAR & FILTERS -->
        ${this.ctxLoad.banner()}
        <div class="inst-toolbar">
          ${this.renderCascadeFilters()}

          <div class="inst-search-box">
            <i data-lucide="search" style="width: 16px; height: 16px;"></i>
            <input type="text" id="inst-search-input" placeholder="Buscar por TAG, nome, fabricante, serviço..." value="${this.searchTerm}">
          </div>

          <select class="inst-select" id="inst-type-filter">
            <option value="ALL" ${this.typeFilter === 'ALL' ? 'selected' : ''}>Todos os Tipos</option>
            <option value="PRESSURE_GAUGE" ${this.typeFilter === 'PRESSURE_GAUGE' ? 'selected' : ''}>Manômetro (PI/PG)</option>
            <option value="PRESSURE_TRANSMITTER" ${this.typeFilter === 'PRESSURE_TRANSMITTER' ? 'selected' : ''}>Transmissor de Pressão (PT)</option>
            <option value="TEMPERATURE_INDICATOR" ${this.typeFilter === 'TEMPERATURE_INDICATOR' ? 'selected' : ''}>Termômetro (TI)</option>
            <option value="TEMPERATURE_TRANSMITTER" ${this.typeFilter === 'TEMPERATURE_TRANSMITTER' ? 'selected' : ''}>Transmissor de Temp. (TT)</option>
            <option value="LEVEL_INDICATOR" ${this.typeFilter === 'LEVEL_INDICATOR' ? 'selected' : ''}>Visor/Indicador de Nível</option>
            <option value="LEVEL_TRANSMITTER" ${this.typeFilter === 'LEVEL_TRANSMITTER' ? 'selected' : ''}>Transmissor de Nível (LT)</option>
            <option value="FLOW_METER" ${this.typeFilter === 'FLOW_METER' ? 'selected' : ''}>Medidor de Vazão</option>
            <option value="SWITCH" ${this.typeFilter === 'SWITCH' ? 'selected' : ''}>Chave / Pressostato</option>
            <option value="OTHER" ${this.typeFilter === 'OTHER' ? 'selected' : ''}>Outros</option>
          </select>

          <select class="inst-select" id="inst-calib-filter">
            <option value="ALL" ${this.calibFilter === 'ALL' ? 'selected' : ''}>Todas as Calibrações</option>
            <option value="VALID" ${this.calibFilter === 'VALID' ? 'selected' : ''}>Válida</option>
            <option value="EXPIRED" ${this.calibFilter === 'EXPIRED' ? 'selected' : ''}>Vencida</option>
            <option value="NOT_REQUIRED" ${this.calibFilter === 'NOT_REQUIRED' ? 'selected' : ''}>Não Requer</option>
          </select>

          <select class="inst-select" id="inst-status-filter">
            <option value="ALL" ${this.statusFilter === 'ALL' ? 'selected' : ''}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
            <option value="OUT_OF_SERVICE" ${this.statusFilter === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
            <option value="INACTIVE" ${this.statusFilter === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
          </select>
        </div>

        <!-- TABLE -->
        <div class="inst-table-wrapper">
          <table class="inst-table">
            <thead>
              <tr>
                <th style="width: 130px;">TAG</th>
                ${showClientColumn ? '<th>Cliente</th>' : ''}
                <th>Nome / Tipo</th>
                <th>Equipamento Vinculado</th>
                <th>Faixa de Medição</th>
                <th>Última Calibração</th>
                <th>Validade / Status</th>
                <th style="width: 100px;">Status</th>
                <th style="width: 210px; text-align: right;">Ações</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="${showClientColumn ? 9 : 8}" style="padding: 48px 16px; text-align: center; color: var(--color-text-primary);">
                    <i data-lucide="gauge" style="width: 40px; height: 40px; margin-bottom: 12px; ;"></i>
                    <p style="margin: 0; font-size: 0.95rem;">Nenhum instrumento cadastrado.</p>
                    <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--color-text-primary);">Cadastre instrumentos técnicos ou ajuste os filtros.</p>
                  </td>
                </tr>
              ` : filtered.map(inst => {
                const linkedEq = this.equipments.find(e => e.id === inst.equipmentId);
                const rangeText = (inst.rangeMin !== null && inst.rangeMax !== null) 
                  ? `${inst.rangeMin} a ${inst.rangeMax} ${inst.unit || ''}`
                  : (inst.unit ? `Unidade: ${inst.unit}` : '-');

                return `
                  <tr>
                    <td class="inst-tag-cell">${inst.tag}</td>
                    ${showClientColumn ? `<td>${this.esc(this.clientNameOf(inst.clientId))}</td>` : ''}
                    <td>
                      <div style="font-weight: 500; color: var(--color-text-primary);">${inst.name}</div>
                      <div style="font-size: 0.75rem; color: var(--color-text-primary);">${this.getTypeLabel(inst.instrumentType)}</div>
                    </td>
                    <td>
                      ${linkedEq ? `
                        <a href="#/equipment/${linkedEq.id}" style="color: var(--ia-info-fg, #60a5fa); text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 4px;">
                          <i data-lucide="boxes" style="width: 12px; height: 12px;"></i> ${linkedEq.tag}
                        </a>
                      ` : `<span style="color: var(--color-text-primary); font-size: 0.8rem;">Não vinculado</span>`}
                    </td>
                    <td><span style="font-size: 0.82rem; font-family: monospace; color: var(--color-text-primary);">${rangeText}</span></td>
                    <td>
                      <div style="font-size: 0.82rem;">${inst.lastCalibrationDate ? new Date(inst.lastCalibrationDate).toLocaleDateString('pt-BR') : '-'}</div>
                      ${inst.certificateNumber ? `<div style="font-size: 0.7rem; color: var(--color-text-primary);">Cert: ${inst.certificateNumber}</div>` : ''}
                    </td>
                    <td>
                      <div style="margin-bottom: 2px;">${this.getCalibrationBadge(inst.calibrationStatus)}</div>
                      ${inst.nextCalibrationDate ? `<div style="font-size: 0.72rem; color: var(--color-text-disabled);">Vence: ${new Date(inst.nextCalibrationDate).toLocaleDateString('pt-BR')}</div>` : ''}
                    </td>
                    <td>${this.getStatusBadge(inst.status)}</td>
                    <td style="text-align: right;">
                      <button class="inst-action-btn btn-view-inst" data-id="${inst.id}" title="Ver Detalhes e Calibrações">
                        <i data-lucide="eye" style="width: 14px; height: 14px;"></i>
                      </button>
                      <button class="inst-action-btn btn-calib-inst" data-id="${inst.id}" title="Registrar Calibração">
                        <i data-lucide="calendar" style="width: 14px; height: 14px;"></i> Calibrar
                      </button>
                      ${canUpdate ? `
                        <button class="inst-action-btn btn-edit-inst" data-id="${inst.id}" title="Editar Instrumento">
                          <i data-lucide="edit-3" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : ''}
                      ${inst.status === 'ACTIVE' ? `
                        <button class="inst-action-btn warning btn-inactivate-inst" data-id="${inst.id}" data-tag="${inst.tag}" title="Inativar">
                          <i data-lucide="power" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : `
                        <button class="inst-action-btn btn-reactivate-inst" data-id="${inst.id}" data-tag="${inst.tag}" title="Reativar" style="color: var(--status-success-text);">
                          <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i>
                        </button>
                      `}
                      ${canDelete ? `
                        <button class="inst-action-btn danger btn-delete-inst" data-id="${inst.id}" data-tag="${inst.tag}" title="Excluir Definitivamente">
                          <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : ''}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <!-- MODAL CONTAINER -->
      <div id="inst-modal-container"></div>
    `;

    createIcons({
      icons: { Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, LinkIcon, Power, ArrowRight, Gauge, Cpu, Calendar, ShieldCheck, Wrench },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private bindEvents() {
    // Cascata da listagem: trocar um nível limpa os de baixo e reconsulta.
    this.querySelectorAll<HTMLSelectElement>('select[data-filter]').forEach(select => {
      select.addEventListener('change', () => {
        this.filter = changeCascade(this.filter, select.dataset['filter'] as CascadeLevel, select.value);
        void this.loadData();
      });
    });

    const searchInput = this.querySelector('#inst-search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchTerm = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector('#inst-type-filter')?.addEventListener('change', (e) => {
      this.typeFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#inst-calib-filter')?.addEventListener('change', (e) => {
      this.calibFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#inst-status-filter')?.addEventListener('change', (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#btn-refresh-list')?.addEventListener('click', () => {
      this.loadData();
    });

    this.querySelector('#btn-goto-hub')?.addEventListener('click', () => {
      router.navigate('/technical-assets');
    });

    this.querySelector('#btn-new-inst')?.addEventListener('click', () => {
      this.openCreateModal();
    });

    this.querySelectorAll('.btn-view-inst').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) router.navigate(`/instruments/${id}`);
      });
    });

    this.querySelectorAll('.btn-calib-inst').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const inst = this.instruments.find(i => i.id === id);
        if (inst) this.openAddCalibrationModal(inst);
      });
    });

    this.querySelectorAll('.btn-edit-inst').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const inst = this.instruments.find(i => i.id === id);
        if (inst) this.openEditModal(inst);
      });
    });

    this.querySelectorAll('.btn-inactivate-inst').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openInactivateModal(id, tag);
      });
    });

    this.querySelectorAll('.btn-reactivate-inst').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && confirm(`Deseja reativar o instrumento ${tag}?`)) {
          try {
            await window.app.platform.instrumentService.reactivateInstrument(this.ctxForInstrument(id), id, "Reativação de instrumento");
            void this.loadData();
          } catch (err) {
            alert(`Erro ao reativar: ${this.errorText(err)}`);
          }
        }
      });
    });

    this.querySelectorAll('.btn-delete-inst').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openDeleteModal(id, tag);
      });
    });
  }

  // CREATE INSTRUMENT MODAL
  private openCreateModal() {
    const modalContainer = this.querySelector('#inst-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 680px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="plus" style="color: var(--color-text-muted); width: 20px; height: 20px;"></i> Cadastrar Novo Instrumento
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-create-inst" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;">
            <div id="inst-cascade-host"></div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG do Instrumento *</label>
                <input type="text" name="tag" required placeholder="Ex: PI-101, TI-201, PT-301" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Instrumento *</label>
                <select name="instrumentType" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="PRESSURE_GAUGE">Manômetro (PI/PG)</option>
                  <option value="PRESSURE_TRANSMITTER">Transmissor de Pressão (PT)</option>
                  <option value="TEMPERATURE_INDICATOR">Termômetro (TI)</option>
                  <option value="TEMPERATURE_TRANSMITTER">Transmissor de Temperatura (TT)</option>
                  <option value="LEVEL_INDICATOR">Indicador/Visor de Nível (LI/LG)</option>
                  <option value="LEVEL_TRANSMITTER">Transmissor de Nível (LT)</option>
                  <option value="FLOW_METER">Medidor de Vazão (FI)</option>
                  <option value="FLOW_TRANSMITTER">Transmissor de Vazão (FT)</option>
                  <option value="SWITCH">Chave / Pressostato</option>
                  <option value="THERMOCOUPLE">Termopar (TE)</option>
                  <option value="RTD">Termorresistência (RTD Pt100)</option>
                  <option value="DIFFERENTIAL_PRESSURE">Pressão Diferencial (PDT)</option>
                  <option value="OTHER">Outro</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
              <input type="text" name="name" required placeholder="Ex: Manômetro de Entrada do Vaso V-101" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Faixa Mínima</label>
                <input type="number" step="any" name="rangeMin" placeholder="0" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Faixa Máxima</label>
                <input type="number" step="any" name="rangeMax" placeholder="25" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Unidade *</label>
                <input type="text" name="unit" required placeholder="bar, kgf/cm², °C" value="bar" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Classe / Exatidão</label>
                <input type="text" name="accuracy" placeholder="CL A (±1.0%)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                <input type="text" name="manufacturer" placeholder="Ex: WIKA, Emerson, Yokogawa" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Modelo</label>
                <input type="text" name="model" placeholder="Ex: 232.50" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº de Série</label>
                <input type="text" name="serialNumber" placeholder="Ex: NS-41098" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Conexão ao Processo</label>
                <input type="text" name="processConnection" placeholder="Ex: 1/2'' NPT Macho, Flange 2'' 150#" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Sinal</label>
                <input type="text" name="signalType" placeholder="Ex: 4-20mA HART, N/A (Local)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações</label>
              <textarea name="notes" rows="2" placeholder="Notas técnicas e especificações adicionais..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-inst">Cancelar</button>
              <button type="submit" class="btn-primary-inst">Salvar Instrumento</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Plus },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    // CLIENTE * → PROJETO → UNIDADE / ÁREA → EQUIPAMENTO, começando pelo
    // recorte que a listagem já tem selecionado.
    const cascade = new AssetCascadeFields(
      modalContainer.querySelector('#inst-cascade-host') as HTMLElement,
      this.cascadeSources(),
      { ...this.filter },
      { lockClient: false, equipmentLabel: "Equipamento Vinculado" }
    );
    void cascade.mount();

    const form = modalContainer.querySelector('#form-create-inst') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      // O Cliente vem do formulário — nunca do cabeçalho nem da sessão.
      const links = cascade.value;
      if (!links.clientId) {
        alert('Selecione o Cliente do Instrumento.');
        return;
      }

      const newInst: Partial<Instrument> = {
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        instrumentType: formData.get('instrumentType') as InstrumentType,
        projectId: links.projectId || null,
        equipmentId: links.equipmentId ? links.equipmentId as EquipmentId : null,
        areaId: links.areaId ? links.areaId as AreaId : null,
        rangeMin: formData.get('rangeMin') ? Number(formData.get('rangeMin')) : null,
        rangeMax: formData.get('rangeMax') ? Number(formData.get('rangeMax')) : null,
        unit: (formData.get('unit') as string) || 'bar',
        accuracy: (formData.get('accuracy') as string) || null,
        manufacturer: (formData.get('manufacturer') as string) || null,
        model: (formData.get('model') as string) || null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        processConnection: (formData.get('processConnection') as string) || null,
        signalType: (formData.get('signalType') as string) || null,
        notes: (formData.get('notes') as string) || null,
        status: 'ACTIVE',
        calibrationRequired: true
      };

      try {
        await window.app.platform.instrumentService.createInstrument(this.ctxFor(links.clientId), newInst);
        closeModal();
        void this.loadData();
      } catch (err) {
        alert(`Erro ao criar instrumento: ${this.errorText(err)}`);
      }
    });
  }

  // EDIT INSTRUMENT MODAL
  private openEditModal(inst: Instrument) {
    const modalContainer = this.querySelector('#inst-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 680px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="edit-3" style="color: var(--color-text-muted); width: 20px; height: 20px;"></i> Editar Instrumento (${inst.tag})
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-edit-inst" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;">
            <div id="inst-cascade-host"></div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG do Instrumento *</label>
                <input type="text" name="tag" required value="${inst.tag}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Instrumento *</label>
                <select name="instrumentType" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="PRESSURE_GAUGE" ${inst.instrumentType === 'PRESSURE_GAUGE' ? 'selected' : ''}>Manômetro (PI/PG)</option>
                  <option value="PRESSURE_TRANSMITTER" ${inst.instrumentType === 'PRESSURE_TRANSMITTER' ? 'selected' : ''}>Transmissor de Pressão (PT)</option>
                  <option value="TEMPERATURE_INDICATOR" ${inst.instrumentType === 'TEMPERATURE_INDICATOR' ? 'selected' : ''}>Termômetro (TI)</option>
                  <option value="TEMPERATURE_TRANSMITTER" ${inst.instrumentType === 'TEMPERATURE_TRANSMITTER' ? 'selected' : ''}>Transmissor de Temperatura (TT)</option>
                  <option value="LEVEL_INDICATOR" ${inst.instrumentType === 'LEVEL_INDICATOR' ? 'selected' : ''}>Indicador/Visor de Nível (LI/LG)</option>
                  <option value="LEVEL_TRANSMITTER" ${inst.instrumentType === 'LEVEL_TRANSMITTER' ? 'selected' : ''}>Transmissor de Nível (LT)</option>
                  <option value="FLOW_METER" ${inst.instrumentType === 'FLOW_METER' ? 'selected' : ''}>Medidor de Vazão (FI)</option>
                  <option value="FLOW_TRANSMITTER" ${inst.instrumentType === 'FLOW_TRANSMITTER' ? 'selected' : ''}>Transmissor de Vazão (FT)</option>
                  <option value="SWITCH" ${inst.instrumentType === 'SWITCH' ? 'selected' : ''}>Chave / Pressostato</option>
                  <option value="THERMOCOUPLE" ${inst.instrumentType === 'THERMOCOUPLE' ? 'selected' : ''}>Termopar (TE)</option>
                  <option value="RTD" ${inst.instrumentType === 'RTD' ? 'selected' : ''}>Termorresistência (RTD Pt100)</option>
                  <option value="DIFFERENTIAL_PRESSURE" ${inst.instrumentType === 'DIFFERENTIAL_PRESSURE' ? 'selected' : ''}>Pressão Diferencial (PDT)</option>
                  <option value="OTHER" ${inst.instrumentType === 'OTHER' ? 'selected' : ''}>Outro</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
              <input type="text" name="name" required value="${inst.name}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Faixa Mínima</label>
                <input type="number" step="any" name="rangeMin" value="${inst.rangeMin ?? ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Faixa Máxima</label>
                <input type="number" step="any" name="rangeMax" value="${inst.rangeMax ?? ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Unidade *</label>
                <input type="text" name="unit" required value="${inst.unit || 'bar'}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Classe / Exatidão</label>
                <input type="text" name="accuracy" value="${inst.accuracy || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                <input type="text" name="manufacturer" value="${inst.manufacturer || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Modelo</label>
                <input type="text" name="model" value="${inst.model || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº de Série</label>
                <input type="text" name="serialNumber" value="${inst.serialNumber || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Conexão ao Processo</label>
                <input type="text" name="processConnection" value="${inst.processConnection || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Sinal</label>
                <input type="text" name="signalType" value="${inst.signalType || ''}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Status</label>
                <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="ACTIVE" ${inst.status === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
                  <option value="OUT_OF_SERVICE" ${inst.status === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
                  <option value="INACTIVE" ${inst.status === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações</label>
              <textarea name="notes" rows="2" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;">${inst.notes || ''}</textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-inst">Cancelar</button>
              <button type="submit" class="btn-primary-inst">Salvar Alterações</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Edit3 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    // Edição: o Cliente PERSISTIDO do instrumento é carregado e fica fixo —
    // nunca o Cliente da sessão nem o do filtro.
    const cascade = new AssetCascadeFields(
      modalContainer.querySelector('#inst-cascade-host') as HTMLElement,
      this.cascadeSources(),
      {
        clientId: String(inst.clientId),
        projectId: inst.projectId ?? "",
        areaId: inst.areaId ?? "",
        equipmentId: inst.equipmentId ?? ""
      },
      { lockClient: true, equipmentLabel: "Equipamento Vinculado" }
    );
    void cascade.mount();

    const form = modalContainer.querySelector('#form-edit-inst') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const links = cascade.value;

      const updated: Partial<Instrument> & { id: string } = {
        id: inst.id,
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        instrumentType: formData.get('instrumentType') as InstrumentType,
        projectId: links.projectId || null,
        equipmentId: links.equipmentId ? links.equipmentId as EquipmentId : null,
        areaId: links.areaId ? links.areaId as AreaId : null,
        rangeMin: formData.get('rangeMin') ? Number(formData.get('rangeMin')) : null,
        rangeMax: formData.get('rangeMax') ? Number(formData.get('rangeMax')) : null,
        unit: (formData.get('unit') as string) || 'bar',
        accuracy: (formData.get('accuracy') as string) || null,
        manufacturer: (formData.get('manufacturer') as string) || null,
        model: (formData.get('model') as string) || null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        processConnection: (formData.get('processConnection') as string) || null,
        signalType: (formData.get('signalType') as string) || null,
        status: (formData.get('status') as InstrumentStatus) || 'ACTIVE',
        notes: (formData.get('notes') as string) || null
      };

      try {
        await window.app.platform.instrumentService.updateInstrument(this.ctxFor(String(inst.clientId)), updated);
        closeModal();
        void this.loadData();
      } catch (err) {
        alert(`Erro ao atualizar instrumento: ${this.errorText(err)}`);
      }
    });
  }

  // ADD CALIBRATION MODAL
  private openAddCalibrationModal(inst: Instrument) {
    const modalContainer = this.querySelector('#inst-modal-container') as HTMLElement;
    if (!modalContainer) return;

    const todayStr = todayLocalDay();
    const nextYearDate = new Date();
    nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
    const nextYearStr = localDayOf(nextYearDate);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 580px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-success-text); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="calendar" style="width: 20px; height: 20px;"></i> Registrar Calibração (${inst.tag})
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-add-calib" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Data da Calibração *</label>
                <input type="date" name="date" required value="${todayStr}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nº do Certificado *</label>
                <input type="text" name="certificate" required placeholder="Ex: CAL-2026-0894" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Resultado *</label>
                <select name="result" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="APPROVED">Aprovado</option>
                  <option value="APPROVED_WITH_RESTRICTION">Aprovado com Restrição</option>
                  <option value="REJECTED">Reprovado</option>
                </select>
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Data Próxima Calibração (Validade)</label>
                <input type="date" name="nextDueDate" value="${nextYearStr}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Responsável Técnico *</label>
                <input type="text" name="responsible" required placeholder="Ex: Eng. Roberto Silva (CREA 123456)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Laboratório / Entidade Calibradora</label>
                <input type="text" name="calibrationEntity" placeholder="Ex: Metrologia Calibra RBC" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Erro Máximo Encontrado</label>
                <input type="text" name="errorMaxFound" placeholder="Ex: +0.02 bar" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Incerteza Expandida (U)</label>
                <input type="text" name="uncertainty" placeholder="Ex: ±0.005 bar (k=2)" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações da Calibração</label>
              <textarea name="notes" rows="2" placeholder="Observações..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-inst">Cancelar</button>
              <button type="submit" class="btn-primary-inst">Salvar Calibração</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Calendar },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-add-calib') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);

      const calibData: any = {
        instrumentId: inst.id,
        date: (formData.get('date') as string),
        certificate: (formData.get('certificate') as string),
        result: (formData.get('result') as string),
        responsible: (formData.get('responsible') as string),
        nextDueDate: (formData.get('nextDueDate') as string) || null,
      };
      if (formData.get('calibrationEntity')) calibData.calibrationEntity = formData.get('calibrationEntity') as string;
      if (formData.get('errorMaxFound')) calibData.errorMaxFound = formData.get('errorMaxFound') as string;
      if (formData.get('uncertainty')) calibData.uncertainty = formData.get('uncertainty') as string;
      if (formData.get('notes')) calibData.notes = formData.get('notes') as string;

      try {
        await window.app.platform.instrumentService.addCalibration(this.ctxFor(String(inst.clientId)), calibData);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao registrar calibração: ${err.message}`);
      }
    });
  }

  // INACTIVATE MODAL
  private openInactivateModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#inst-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 480px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-text); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="power" style="width: 20px; height: 20px;"></i> Inativar Instrumento ${tag}
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-inactivate-inst" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <p style="margin: 0; font-size: 0.875rem; color: var(--color-text-primary);">
              A inativação preservará todos os certificados e calibrações anteriores, alterando o status do instrumento para <strong>INATIVO</strong>.
            </p>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Inativação *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Instrumento danificado ou substituído na bancada..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-inst">Cancelar</button>
              <button type="submit" style="background: var(--status-warning-text); color: var(--color-text-inverse); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Confirmar Inativação</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Power },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-inactivate-inst') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;

      try {
        await window.app.platform.instrumentService.inactivateInstrument(this.ctxForInstrument(id), id, reason);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao inativar instrumento: ${err.message}`);
      }
    });
  }

  // DELETE MODAL (CONTROLLED HARD DELETE)
  private openDeleteModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#inst-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="trash-2" style="width: 20px; height: 20px;"></i> Exclusão de Instrumento
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-delete-inst" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 12px; color: var(--status-danger-border); font-size: 0.85rem;">
              <strong>Atenção:</strong> A exclusão definitiva será rejeitada se este instrumento possuir histórico de calibração ou documentos vinculados.
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Exclusão de ${tag} *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Cadastro duplicado..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-inst">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Excluir Instrumento</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Trash2 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-delete-inst') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;

      try {
        const res = await window.app.platform.instrumentService.deleteInstrument(this.ctxForInstrument(id), id, reason);
        alert(res.message);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(err.safeMessage || err.message);
      }
    });
  }
}

if (!customElements.get("app-instruments-page")) {
  customElements.define("app-instruments-page", AppInstrumentsPage);
}




